import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  RouteReuseStrategy,
  withHashLocation,
  withRouterConfig
} from '@angular/router';
import {
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MissingTranslationHandler,
  provideTranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { MsPersonalAuthService } from './services/ms-personal-auth.service';
import { AuthPostLoginService } from './services/other/auth-post-login';
import { DedupMessageService } from './services/other/dedup-message.service';
import { FeatureFlagsService } from './services/other/feature-flags.service';
import { TawkChatService } from './services/other/tawk-chat.service';
import { HttpInterceptorService } from './services/other/http-interceptor';
import { HumanizeMissingTranslationHandler } from './services/other/humanize-missing-translation';
import { ToastService } from './services/other/toast';
import { NotificationToastBridge } from './services/notifications/notification-toast-bridge.service';
import { TabGroupRouteReuseStrategy } from './services/other/tab-group-route-reuse-strategy';
import { MyPreset } from './themes/theme';

export function MSALInstanceFactory() {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Zone.js change detection (with event coalescing). PrimeNG 20's Table
    // and several other components inject `NgZone` directly, so the app
    // can't run zoneless until those are replaced. Switching back to
    // `provideZonelessChangeDetection()` once PrimeNG removes those
    // dependencies is a one-line change.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withHashLocation(),
      // withViewTransitions(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    { provide: RouteReuseStrategy, useClass: TabGroupRouteReuseStrategy },
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideTranslateService({
      lang: 'en',
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
      missingTranslationHandler: HumanizeMissingTranslationHandler,
    }),
    {
      provide: MissingTranslationHandler,
      useClass: HumanizeMissingTranslationHandler,
    },
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    HttpInterceptorService,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: HttpInterceptorService,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // Dedup wrapper over PrimeNG's MessageService: every toast in the app
    // funnels through here, so identical error/warn bursts (e.g. 8 dashboard
    // requests failing on one outage) collapse to a single toast globally.
    { provide: MessageService, useClass: DedupMessageService },
    ToastService,
    DialogService,
    ConfirmationService,
    DynamicDialogRef,
    provideAppInitializer(async () => {
      const authPostLoginService = inject(AuthPostLoginService);
      const msPersonalAuthService = inject(MsPersonalAuthService);
      // Injected BEFORE the first await, like the bridge below — after an await the injection
      // context is gone. Which deployment this is has to be known before the login screen paints,
      // or a build pointed at a no-Microsoft backend would flash a "Sign in with Microsoft" button
      // that cannot work.
      const featureFlags = inject(FeatureFlagsService);
      // Instantiate the SignalR→toast bridge synchronously (BEFORE the await).
      // After `await`, the injection context is lost — `inject()` would throw NG0203.
      // The bridge's constructor effect watches `currentUser()` and (re)subscribes
      // on login / enterprise switch.
      inject(NotificationToastBridge);
      // Tawk.to live support: loads the widget now, so it is there on the login screen too.
      inject(TawkChatService);

      // Resolves even when the endpoint is missing or unreachable — it then leaves every feature
      // enabled, so an older backend changes nothing about how the app behaves.
      await featureFlags.load();

      // A Microsoft sign-in that went by full-page redirect (mobile browsers) comes back as a
      // fragment on the redirect URI. It has to be read here, before the router's first navigation
      // treats that fragment as a route and replaces it. On any other page load this is a no-op.
      // When it does establish a session, the exchange has already run the post-login setup.
      if (await msPersonalAuthService.completeRedirectLogin()) {
        return;
      }
      // Pages that must not start the stored session: the verification page is public, and sign-up
      // signs the stored user out (signupInviteGuard). Checked here, not in handlePostLogin, because
      // the sign-up page calls that itself to start the session of the person who just signed up.
      const route = window.location.hash.replace('#/', '');
      if (['verify', 'sign-up'].some((page) => route.startsWith(page))) {
        return;
      }
      await authPostLoginService.handlePostLogin();
    }),
  ],
};
